import { api, ApiError } from './api';
import { recaptchaHeader } from './captcha';
import type { Me } from '../api/client';
import { getDeviceId } from '../utils/device';
import { authDeviceClass } from '../utils/viewport';

// In-memory access-token storage. Per the auth-package contract, the
// short-lived JWT rides in the Authorization: Bearer header on every
// protected request; the long-lived refresh_token is in an HttpOnly
// cookie scoped to /api/auth and JS can't read it. On reload, we
// silently exchange the cookie for a fresh access token via POST
// /api/auth/refresh.
let accessToken: string | null = null;
let cachedMe: Me | null = null;

interface VerifyOrRefreshResponse {
  accessToken: string;
  user?: { id: string; email: string; plan?: string; isNew?: boolean };
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// Session id of the device we're signed in on, read from the access token's
// `sid` claim. The session-list endpoint can't identify the current session
// server-side — the refresh_token cookie that would mark it is path-scoped to
// /api/auth and isn't sent to /api/users/me/sessions — so the SPA matches
// `sid` against each session's id instead. Returns null when there's no token
// or it can't be decoded (the JWT is already trusted; we only read a claim).
export function currentSessionId(): string | null {
  if (!accessToken) return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { sid?: unknown };
    return typeof claims.sid === 'string' ? claims.sid : null;
  } catch {
    return null;
  }
}

export async function authInit(): Promise<void> {
  // 1. Probe /api/me first. The safe GET also seeds the CSRF token
  //    (captured by the api wrapper from the X-CSRF-Token response
  //    header) so subsequent POSTs land. If we already have an in-memory
  //    access token from a verify in this session, /api/me will return
  //    200 here.
  try {
    cachedMe = await api<Me>('/api/me');
    return;
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) {
      cachedMe = null;
      accessToken = null;
      return;
    }
    // 401 just means no session yet — fall through to the silent
    // refresh path.
  }

  // 2. Try POST /api/auth/refresh — the HttpOnly refresh_token cookie
  //    survives reload, so the server can hand us a fresh access token
  //    without bouncing the user back to /login. CSRF is in place after
  //    step 1.
  try {
    const resp = await api<VerifyOrRefreshResponse>('/api/auth/refresh', {
      method: 'POST',
      body: { deviceId: getDeviceId() },
      // The server compares this against the session's bound class; a change
      // forces re-auth (DEVICE_CLASS_CHANGED). authDeviceClass mirrors the
      // server's derivation, so an unchanged device matches the bound
      // class and only a genuine class switch trips the check.
      headers: { 'X-Device-Class': authDeviceClass() },
    });
    if (resp?.accessToken) accessToken = resp.accessToken;
  } catch {
    // No valid refresh cookie — the user is genuinely logged out.
    accessToken = null;
    cachedMe = null;
    return;
  }

  // 3. Re-probe /api/me with the fresh access token.
  try {
    cachedMe = await api<Me>('/api/me');
  } catch {
    accessToken = null;
    cachedMe = null;
  }
}

export function isAuthenticated(): boolean {
  return cachedMe !== null;
}

export function currentUser(): Me | null {
  return cachedMe;
}

export async function requestMagicLink(email: string): Promise<void> {
  await api('/api/auth/magic-link', {
    method: 'POST',
    body: { email },
    headers: await recaptchaHeader('magic_link'),
  });
}

// requestAccountRecovery starts the lost-passkey flow. Like the magic
// link, the server returns the same generic response for any email (no
// enumeration); a 429 surfaces as ApiError for the caller to handle.
export async function requestAccountRecovery(email: string): Promise<void> {
  await api('/api/auth/account-recovery', { method: 'POST', body: { email } });
}

export type OAuthProvider = 'google' | 'apple';

// Begin an OAuth sign-in. Unlike the magic-link POST this is a full-page
// navigation, not a fetch: the init endpoint 302s to the provider's consent
// screen, the provider redirects to /api/auth/oauth/{provider}/callback
// (handled server-side, which sets the refresh cookie), and the browser lands
// back on /?auth=success — where authInit() exchanges the cookie for a token on
// the next boot. deviceId rides along so the session is pinned to this browser
// for the MaxSessions cap (same id used by verify + refresh).
export function startOAuth(provider: OAuthProvider): void {
  const deviceId = encodeURIComponent(getDeviceId());
  // The server derives the authoritative device_class from User-Agent +
  // viewportWidth; both ride through the encrypted OAuth state to the
  // callback. deviceClass is a transitional fallback the server ignores.
  const viewportWidth = encodeURIComponent(String(window.innerWidth));
  const deviceClass = encodeURIComponent(authDeviceClass());
  window.location.assign(
    `/api/auth/oauth/${provider}?deviceId=${deviceId}&viewportWidth=${viewportWidth}&deviceClass=${deviceClass}`,
  );
}

// Maps the auth error codes the OAuth callback can hand back via
// ?auth=error&code=… (app.ts normalizes that landing to /login?error=…) to
// user-facing copy. Unknown codes fall back to a generic message that nudges
// toward the always-available magic-link path.
export function getAuthErrorMessage(code: string): string {
  switch (code) {
    case 'OAUTH_DENIED':
      return 'Sign-in was cancelled. Try again, or use a magic link instead.';
    case 'OAUTH_EMAIL_MISSING':
      return 'Your provider didn’t share an email address, which we need to sign you in. Try a magic link instead.';
    case 'OAUTH_EMAIL_UNVERIFIED':
      return 'Your Google email isn’t verified yet. Verify it with Google, or use a magic link.';
    case 'DEVICE_LIMIT_EXCEEDED':
      return 'You already have 3 active sessions. Revoke one under Settings → Devices to sign in here.';
    case 'OAUTH_STATE_INVALID':
      return 'That sign-in attempt expired or was invalid. Please try again.';
    default:
      return 'Something went wrong signing in. Try again, or use a magic link.';
  }
}

export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } finally {
    accessToken = null;
    cachedMe = null;
  }
}
